import { useRef } from "react";
import { Link } from "react-router-dom";
import type { ArticleItem } from "@/mocks/articles";
import { formatDate } from "@/lib/content";

interface StarTickerProps {
  items: ArticleItem[];
}

export default function StarTicker({ items }: StarTickerProps) {
  const scrollerRef = useRef<HTMLDivElement>(null);

  const scrollBy = (direction: number) => {
    const node = scrollerRef.current;
    if (!node) return;
    node.scrollBy({ left: direction * 320, behavior: "smooth" });
  };

  return (
    <section aria-label="明星动态滚动栏" className="rounded-lg border border-background-200 bg-accent-50 p-4 md:p-6">
      <div className="mb-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent-500 text-background-50">
            <i className="ri-radar-line text-xl"></i>
          </span>
          <div>
            <h2 className="font-heading text-lg font-black text-foreground-950 md:text-xl">
              明星动态滚动栏
            </h2>
            <p className="mt-1 text-xs text-foreground-600 md:text-sm">
              最新明星新闻，横向滑动即可查看
            </p>
          </div>
        </div>
        <div className="hidden items-center gap-2 sm:flex">
          <button
            type="button"
            aria-label="向左滚动"
            onClick={() => scrollBy(-1)}
            className="flex h-9 w-9 items-center justify-center rounded-md border border-accent-200 bg-background-50 text-foreground-700 transition-colors hover:text-accent-700"
          >
            <i className="ri-arrow-left-s-line text-lg"></i>
          </button>
          <button
            type="button"
            aria-label="向右滚动"
            onClick={() => scrollBy(1)}
            className="flex h-9 w-9 items-center justify-center rounded-md border border-accent-200 bg-background-50 text-foreground-700 transition-colors hover:text-accent-700"
          >
            <i className="ri-arrow-right-s-line text-lg"></i>
          </button>
        </div>
      </div>

      <div
        ref={scrollerRef}
        className="no-scrollbar flex snap-x snap-mandatory gap-3 overflow-x-auto pb-1"
      >
        {items.map((item) => (
          <Link
            key={item.id}
            to={`/detail/${item.id}`}
            className="group flex w-[260px] shrink-0 snap-start flex-col rounded-lg border border-accent-200 bg-background-50 p-4 transition-colors hover:border-accent-400 md:w-[300px]"
          >
            <span className="mb-2 w-fit rounded-full bg-accent-100 px-2 py-0.5 text-[11px] font-medium text-accent-900">
              {item.tags[0] || "动态"}
            </span>
            <h3 className="line-clamp-2 font-heading text-sm font-bold leading-snug text-foreground-950 group-hover:text-accent-700 md:text-base">
              {item.title}
            </h3>
            <p className="mt-2 line-clamp-2 text-xs leading-relaxed text-foreground-600">
              {item.summary}
            </p>
            <span className="mt-3 flex items-center gap-1 text-[11px] text-foreground-500">
              <i className="ri-time-line"></i>
              {formatDate(item.date)}
            </span>
          </Link>
        ))}
      </div>
      <p className="mt-3 text-center text-[11px] text-foreground-500 sm:hidden">
        左右滑动查看更多动态
      </p>
    </section>
  );
}